// Offering service module — port of customer-apps/offering/{offering,bookable-offering,
// training-offering}.service.ts. URL paths, HTTP methods and payload fields are the API contract
// and are taken over 1:1 (APP_MIGRATE.md §11).
//
// The type-specific services (course/exam/generic) only add `/offerings/<type>/appointments`
// variants that the club-member-app never calls — its views inject the generic `OfferingService`.
// They are left out until an app needs them.
import { ref } from 'vue';
import { apiFetch, unwrap } from '@shared/http/client';
import type { DataResult } from '@shared/http/client';
import { offeringFiltersToApiQueryParams, toQueryString } from '@shared/offering-filters';
import type {
    BookableOfferingAppointment,
    ExamOffering,
    Instructor,
    LocationArea,
    LocationAreaField,
    Offering,
    OfferingAppointment,
    OfferingBooking,
    OfferingBookingGuestDto,
    OfferingCategory,
    OfferingFilters,
    OfferingLocation,
    OfferingPricing,
    SelectedSalesListOfferingItem,
    Sport,
    TrainingAppointmentResponse,
    TrainingOfferingAppointment,
    WaitListEntry,
    BookingStateValue,
} from '@shared/types/offering';
import { BookingState } from '@shared/types/offering';
import type { SepaMandate } from '@shared/components/accounting/SepaMandateControl.vue';

export interface IdAndName {
    id: number
    name: string
}

function memberIdParam(filterMemberId?: number): string {
    return filterMemberId ? `?${toQueryString({ filter_member_id: filterMemberId })}` : '';
}

export async function getOfferingAppointments<T extends OfferingAppointment>(
    filters: OfferingFilters,
    page = 1,
    perPage: number,
    filterMemberId?: number,
): Promise<DataResult<T[]>> {
    const params: Record<string, string | number | (string | number)[]> = {
        ...offeringFiltersToApiQueryParams(filters),
        page,
        per_page: perPage,
    };
    if (filterMemberId) {
        params.filter_member_id = filterMemberId;
    }

    return apiFetch<DataResult<T[]>>(`/offerings/appointments?${toQueryString(params)}`);
}

export async function getOfferingAppointment<T extends OfferingAppointment>(appointmentSeriesId: string, start: string): Promise<T> {
    const result = await apiFetch<DataResult<T>>(`/offerings/appointments/${appointmentSeriesId}/start/${start}`);
    return unwrap(result) as T;
}

export async function getSeriesAppointmentsForOfferingId<T extends OfferingAppointment>(offeringId: string): Promise<T[]> {
    const result = await apiFetch<DataResult<T[]>>(`/offerings/offering/${offeringId}/appointments`);
    return unwrap(result) as T[];
}

export async function getOfferingCategories(filterMemberId?: number): Promise<OfferingCategory[]> {
    const result = await apiFetch<DataResult<OfferingCategory[]>>(`/offerings/categories${memberIdParam(filterMemberId)}`);
    return unwrap(result) as OfferingCategory[];
}

export async function getSports(filterMemberId?: number): Promise<Sport[]> {
    const result = await apiFetch<DataResult<Sport[]>>(`/offerings/sports${memberIdParam(filterMemberId)}`);
    return unwrap(result) as Sport[];
}

export async function getOfferingDepartments(filterMemberId?: number): Promise<IdAndName[]> {
    const result = await apiFetch<DataResult<IdAndName[]>>(`/offerings/departments${memberIdParam(filterMemberId)}`);
    return unwrap(result) as IdAndName[];
}

export async function getOfferingTeams(filterMemberId?: number): Promise<IdAndName[]> {
    const result = await apiFetch<DataResult<IdAndName[]>>(`/offerings/teams${memberIdParam(filterMemberId)}`);
    return unwrap(result) as IdAndName[];
}

export async function getInstructors(filterMemberId?: number): Promise<Instructor[]> {
    const result = await apiFetch<DataResult<Instructor[]>>(`/offerings/instructors${memberIdParam(filterMemberId)}`);
    return unwrap(result) as Instructor[];
}

export async function getOfferingLocations(filterMemberId?: number): Promise<OfferingLocation[]> {
    const result = await apiFetch<DataResult<OfferingLocation[]>>(`/offerings/locations${memberIdParam(filterMemberId)}`);
    return unwrap(result) as OfferingLocation[];
}

export async function toggleFavorite(offering: Offering): Promise<Offering> {
    const url = `/offerings/${offering.id}/favorite`;
    await apiFetch<void>(url, { method: offering.isFavorite ? 'DELETE' : 'POST', body: offering.isFavorite ? undefined : '{}' });
    return { ...offering, isFavorite: !offering.isFavorite };
}

export function getAreasFromLocations(locations: OfferingLocation[]): LocationArea[] {
    return locations.reduce((areas: LocationArea[], location) => areas.concat(location.areas), []);
}

export function getFieldsFromLocations(locations: OfferingLocation[]): LocationAreaField[] {
    return getAreasFromLocations(locations).reduce((fields: LocationAreaField[], area) => fields.concat(area.fields), []);
}

// --- bookings & wait list -------------------------------------------------------------------

export async function bookAppointment(
    appointment: BookableOfferingAppointment,
    memberId: number,
    invoiceMemberId: number,
    invoiceSepa?: SepaMandate | null,
    comment?: string | null,
    dogId?: string | null,
    clubExamIds?: string[] | null,
    selectedSalesListOfferingItems?: SelectedSalesListOfferingItem[],
): Promise<BookableOfferingAppointment> {
    const result = await apiFetch<DataResult<BookableOfferingAppointment>>('/offering-bookings', {
        method: 'POST',
        body: JSON.stringify({
            appointmentSeriesId: appointment.appointmentSeries.id,
            start: appointment.start,
            memberId,
            invoiceMemberId,
            invoiceSepa,
            comment,
            dogId,
            clubExamIds,
            selectedSalesListOfferingItems,
        }),
    });
    return unwrap(result) as BookableOfferingAppointment;
}

export async function createGuestAndBookAppointmentForGuest(
    appointment: BookableOfferingAppointment,
    guest: OfferingBookingGuestDto,
    invoiceMemberId: number,
    invoiceSepa?: SepaMandate | null,
    comment?: string | null,
    dogId?: string | null,
    clubExamIds?: string[] | null,
    selectedSalesListOfferingItems?: SelectedSalesListOfferingItem[],
): Promise<BookableOfferingAppointment> {
    const result = await apiFetch<DataResult<BookableOfferingAppointment>>('/offering-booking-with-child-data', {
        method: 'POST',
        body: JSON.stringify({
            appointmentSeriesId: appointment.appointmentSeries.id,
            start: appointment.start,
            invoiceMemberId,
            invoiceSepa,
            comment,
            dogId,
            clubExamIds,
            selectedSalesListOfferingItems,
            ...guest,
        }),
    });
    return unwrap(result) as BookableOfferingAppointment;
}

export async function addToAppointmentWaitList(
    appointment: BookableOfferingAppointment,
    memberId: number,
): Promise<BookableOfferingAppointment> {
    const result = await apiFetch<DataResult<BookableOfferingAppointment>>('/offering-waitlist', {
        method: 'POST',
        body: JSON.stringify({
            appointmentSeriesId: appointment.appointmentSeries.id,
            start: appointment.start,
            memberId,
        }),
    });
    return unwrap(result) as BookableOfferingAppointment;
}

export async function createGuestAndAddToAppointmentWaitList(
    appointment: BookableOfferingAppointment,
    guest: OfferingBookingGuestDto,
): Promise<BookableOfferingAppointment> {
    const result = await apiFetch<DataResult<BookableOfferingAppointment>>('/offering-waitlist-with-child-data', {
        method: 'POST',
        body: JSON.stringify({
            appointmentSeriesId: appointment.appointmentSeries.id,
            start: appointment.start,
            ...guest,
        }),
    });
    return unwrap(result) as BookableOfferingAppointment;
}

// Angular's BookableOfferingService pushes into a `refreshWaitListEntries$` subject after a
// cancellation so the app-wide wait-list banner reloads. The subject is a singleton in the DI root;
// the module-level counter here plays the same role — consumers watch it.
export const waitListRefreshTick = ref(0);

export function refreshWaitListEntries(): void {
    waitListRefreshTick.value += 1;
}

export async function cancelBooking(bookingId: string): Promise<BookableOfferingAppointment> {
    const result = await apiFetch<DataResult<BookableOfferingAppointment>>(`/offering-bookings/${bookingId}`, { method: 'DELETE' });
    refreshWaitListEntries();
    return unwrap(result) as BookableOfferingAppointment;
}

export async function cancelWaitListEntry(waitListEntryId: string): Promise<BookableOfferingAppointment> {
    const result = await apiFetch<DataResult<BookableOfferingAppointment>>(`/offering-waitlist/${waitListEntryId}`, { method: 'DELETE' });
    refreshWaitListEntries();
    return unwrap(result) as BookableOfferingAppointment;
}

export async function getWaitListEntries(): Promise<WaitListEntry[]> {
    const result = await apiFetch<DataResult<WaitListEntry[]>>('/offering-waitlist');
    return unwrap(result) as WaitListEntry[];
}

// --- predicates & derived state -------------------------------------------------------------

export function waitListEntryIsCurrentlyBookable(waitListEntry: WaitListEntry): boolean {
    return !!waitListEntry.reservedUntil && new Date(waitListEntry.reservedUntil).getTime() > Date.now();
}

export function appointmentHasStarted(appointment: OfferingAppointment): boolean {
    return new Date(appointment.start).getTime() < Date.now();
}

export function bookablePeriodHasEnded(appointment: OfferingAppointment | undefined): boolean {
    if (!appointment?.bookingEnd) {
        return false;
    }
    return Date.now() > new Date(appointment.bookingEnd).getTime();
}

export function bookablePeriodHasStarted(appointment: OfferingAppointment | undefined): boolean {
    if (!appointment?.bookingStart) {
        return true;
    }
    return Date.now() > new Date(appointment.bookingStart).getTime();
}

export function bookablePeriodIsActive(appointment: OfferingAppointment | undefined): boolean {
    return bookablePeriodHasStarted(appointment) && !bookablePeriodHasEnded(appointment);
}

export function getBookableBookingsForAppointment(appointment: BookableOfferingAppointment): OfferingBooking[] {
    return appointment.bookings.filter((booking) => !booking.createdAt || booking.deletedAt);
}

export function getActiveBookingsForAppointment(appointment: BookableOfferingAppointment): OfferingBooking[] {
    return appointment.bookings.filter((booking) => booking.createdAt && !booking.deletedAt);
}

export function appointmentHasActiveBookings(appointment: BookableOfferingAppointment): boolean {
    return getActiveBookingsForAppointment(appointment).length > 0;
}

function getCanceledBookingsForAppointment(appointment: BookableOfferingAppointment): OfferingBooking[] {
    return appointment.bookings.filter((booking) => !!booking.deletedAt);
}

export function appointmentIsCompletelyCanceled(appointment: BookableOfferingAppointment): boolean {
    if (appointmentHasActiveBookings(appointment)) {
        return false;
    }
    if (getCanceledBookingsForAppointment(appointment).length === 0) {
        return false;
    }
    return true;
}

export function getMemberPricing(appointment: BookableOfferingAppointment, memberId: number): OfferingPricing | null {
    return appointment.memberPrices.find((memberPrice) => memberPrice.memberId === memberId) ?? null;
}

export function getBookingStateForAppointmentAndMemberId(
    appointment: BookableOfferingAppointment,
    memberId: number,
): BookingStateValue {
    const bookingForMember = appointment.bookings?.find((booking) => booking.member.id === memberId);
    const waitListEntryForMember = appointment.waitListEntries?.find((waitListEntry) => waitListEntry.member.id === memberId);

    if (bookingForMember && bookingForMember.createdAt !== null && bookingForMember.deletedAt === null) {
        return BookingState.booked;
    } else if (bookingForMember && bookingForMember.createdAt !== null && bookingForMember.deletedAt !== null) {
        return BookingState.canceled;
    } else if (appointment.waitListActive && waitListEntryForMember) {
        return waitListEntryIsCurrentlyBookable(waitListEntryForMember)
            ? BookingState.waitListCurrentlyBookable
            : BookingState.waitListWaiting;
    }

    return BookingState.open;
}

// --- training ---------------------------------------------------------------------------------

export async function replyToTrainingAppointment(
    appointment: TrainingOfferingAppointment,
    memberId: number,
    response: TrainingAppointmentResponse,
    comment?: string | null,
): Promise<TrainingOfferingAppointment> {
    const result = await apiFetch<DataResult<TrainingOfferingAppointment>>('/training-response', {
        method: 'POST',
        body: JSON.stringify({
            appointmentSeriesId: appointment.appointmentSeries.id,
            start: appointment.start,
            memberId,
            response,
            comment,
        }),
    });
    return unwrap(result) as TrainingOfferingAppointment;
}

export function countTrainingReplies(appointment: TrainingOfferingAppointment, response: TrainingAppointmentResponse): number {
    return appointment.replies.filter((reply) => reply.response === response).length;
}

export function isExamOffering(offering: Offering): offering is ExamOffering {
    return offering.type === 'exam';
}
